import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import {
  searchResultReducerState,
  SearchResultItem,
  useSearchResultState,
} from "@made-people/grease-search";
import {
  createRandomSeed,
  seededShuffle,
} from "../../../../utils/seeded-shuffle";

export type RandomizedSearchResultItemsProps = PropsWithChildren;

interface ShuffleCache {
  items: SearchResultItem[];
  shuffled: SearchResultItem[];
}

const isAppendOf = (
  previousItems: SearchResultItem[],
  nextItems: SearchResultItem[],
) =>
  nextItems.length >= previousItems.length &&
  previousItems.every((item, index) => item === nextItems[index]);

export const RandomizedSearchResultItems = ({
  children,
}: RandomizedSearchResultItemsProps) => {
  const state = useSearchResultState();
  const [seed, setSeed] = useState<number | null>(null);
  const cacheRef = useRef<ShuffleCache | null>(null);

  useEffect(() => {
    setSeed(createRandomSeed());
  }, []);

  const value = useMemo(() => {
    if (!state.result || seed === null) {
      cacheRef.current = null;
      return state;
    }

    const items = state.result.items;
    const cache = cacheRef.current;

    const shuffled =
      cache && isAppendOf(cache.items, items)
        ? [
            ...cache.shuffled,
            ...seededShuffle(
              items.slice(cache.items.length),
              seed + cache.items.length,
            ),
          ]
        : seededShuffle(items, seed);

    cacheRef.current = { items, shuffled };

    return {
      ...state,
      result: {
        ...state.result,
        items: shuffled,
      },
    };
  }, [state, seed]);

  return (
    <searchResultReducerState.Provider value={value}>
      {children}
    </searchResultReducerState.Provider>
  );
};
